import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { Printer, ChevronLeft, ChevronRight, Clock, AlertCircle, Pencil, RotateCcw, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useShuttleData, useSaveShuttleLine } from '@/api/resources';
import type { ShuttleEmployeeLine, ShuttleLeaveLine, ShuttleAbsenceLine } from '@/api/resources';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function fmt(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export default function ShuttlePage() {
  const now = new Date();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [year, setYear] = useState(prevYear);
  const [month, setMonth] = useState(prevMonth);

  const { data, isLoading, error } = useShuttleData(year, month);
  const monthLabel = `${MONTHS[month - 1]} ${year}`;

  const prev = () => { const m = month === 1 ? 12 : month - 1; setYear(month === 1 ? year - 1 : year); setMonth(m); };
  const next = () => { const m = month === 12 ? 1 : month + 1; setYear(month === 12 ? year + 1 : year); setMonth(m); };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">Fiche navette mensuelle <PageHelp topic="shuttle" /></h1>
            <div className="text-[11px] text-slate-500 mt-0.5">Heures supplémentaires par semaine, congés et absences du mois.</div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            <button onClick={prev} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Mois précédent"><ChevronLeft size={16} /></button>
            <div className="px-3 text-[14px] font-semibold text-slate-900 w-36 text-center tabular-nums">{monthLabel}</div>
            <button onClick={next} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Mois suivant"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/shuttle/history" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-100 text-[12px] text-slate-600 font-medium"><Clock size={13} /> Historique</a>
          <Button variant="primary" icon={Printer} disabled={!data || isLoading} onClick={() => window.print()}>Imprimer / PDF</Button>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 bg-danger-bg border border-danger/30 rounded-lg p-3 text-[12px] text-danger flex items-center gap-2 no-print">
          <AlertCircle size={14} /> {(error as Error).message}
        </div>
      )}

      <div className="flex-1 overflow-auto p-8 bg-slate-100">
        {isLoading ? (
          <div className="text-[13px] text-slate-500 text-center py-12">Calcul de la fiche navette…</div>
        ) : data ? (
          <>
          <MonthSummary lines={data.lines} monthLabel={monthLabel} />
          <div id="fiche-print" className="bg-white mx-auto shadow-sm border border-slate-200 p-8 text-[11px] text-slate-800" style={{ maxWidth: 1100 }}>
            {/* En-tête */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[13px]"><span className="font-semibold">RAISON SOCIALE :</span> {data.pharmacy.name}</div>
                {data.pharmacy.address && <div className="text-[11px] text-slate-500">{data.pharmacy.address}</div>}
              </div>
              <div className="text-[11px]">Date : <span className="inline-block border-b border-slate-400 w-40">&nbsp;</span></div>
            </div>
            <div className="text-[14px] font-bold mb-4">
              Salaires du mois de : <span className="uppercase">{MONTHS[month - 1]} {year}</span>
            </div>

            <FicheTable weeks={data.weeks} lines={data.lines} year={year} month={month} />

            {/* Notes */}
            <div className="mt-5 text-[9.5px] text-slate-500 leading-relaxed space-y-1">
              <div><strong>(1) Décompte par semaine :</strong> écart au contrat noté par semaine (n° ISO), signé : <span className="text-primary font-semibold">+</span> heures supplémentaires, <span className="text-danger font-semibold">−</span> heures dues (en moins). Les semaines d'une rotation (ex. 37h/33h) se <strong>compensent</strong> : le total du mois est net.</div>
              <div><strong>Heures complémentaires :</strong> concernent les temps partiel (maximum 1/10 de l'horaire contractuel).</div>
              <div><strong>Heures supplémentaires — majorations :</strong> 25 % de la 36ᵉ à la 43ᵉ heure · 50 % à partir de la 44ᵉ heure.</div>
            </div>
          </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Résumé du mois (demande du boss) — les totaux étaient calculés par salarié mais
 * jamais agrégés : il fallait additionner la colonne Balance à la main.
 *
 * Même convention que `FicheRow` : on lit `l.balance`, qui est déjà le calcul OU
 * la valeur déclarée à la main. Le résumé doit dire la même chose que le tableau.
 * Case « À récup. » cochée : la balance, positive OU négative, est reportée au
 * mois suivant — rien ne se règle sur la paie. Sinon elle part en paie (positive)
 * ou en heures dues (négative).
 * Volontairement HORS de la zone imprimée : la fiche navette reste le document officiel inchangé.
 */
function MonthSummary({ lines, monthLabel }: { lines: ShuttleEmployeeLine[]; monthLabel: string }) {
  const r1 = (n: number) => Math.round(n * 10) / 10;

  let overtime = 0;   // heures supp du mois, qu'elles soient payées ou reportées
  let toPay = 0;      // heures supp à payer ce mois
  let toRecover = 0;  // balance reportée au mois suivant (net : supp − dues)
  let owed = 0;       // heures dues (en moins) retenues ce mois — R13
  for (const l of lines) {
    const balance = l.balance;
    if (balance > 0) overtime += balance;
    if (l.toRecover) toRecover += balance;
    else if (balance > 0) toPay += balance;
    else if (balance < 0) owed += balance;
  }
  overtime = r1(overtime);

  // Solde cumulé de l'équipe à la fin du mois : c'est le « où on en est » qui
  // manquait — le tableau ne montrait que le net du mois, jamais le report.
  const cumul = r1(lines.reduce((sum, l) => sum + l.balanceAfter, 0));

  const cards = [
    { label: 'Heures supplémentaires', value: overtime, sub: 'total du mois, report inclus', tone: 'text-primary' },
    // Peut être négatif (heures dues reportées) : le libellé dit le report, pas
    // des heures « à récupérer » par le salarié.
    { label: 'Reporté (« À récup. »)', value: r1(toRecover), sub: 'au mois suivant, rien sur la paie', tone: 'text-amber-600' },
    { label: 'À payer', value: r1(toPay), sub: 'à verser sur la paie du mois', tone: 'text-emerald-700' },
    { label: 'Heures dues', value: r1(owed), sub: 'heures en moins à rattraper', tone: 'text-danger' },
    {
      label: 'Solde cumulé',
      value: cumul,
      sub: 'depuis le début, fin de ce mois',
      tone: cumul < 0 ? 'text-danger' : 'text-primary',
    },
  ];

  return (
    <div className="mx-auto mb-4 no-print" style={{ maxWidth: 1100 }}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium mb-2">
        Résumé de {monthLabel}
      </div>
      <div className="grid grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className={`text-[24px] font-semibold tracking-tight tabular-nums leading-none ${c.value === 0 ? 'text-slate-300' : c.tone}`}>
              {c.value > 0 ? `+${c.value}` : c.value}
              <span className="text-[14px] font-medium">h</span>
            </div>
            <div className="text-[11.5px] text-slate-700 mt-1.5 font-medium">{c.label}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FicheTable({ weeks, lines, year, month }: { weeks: number[]; lines: ShuttleEmployeeLine[]; year: number; month: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[10.5px]" style={{ minWidth: 900 }}>
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-300 px-2 py-1.5 text-left align-bottom" rowSpan={2}>Noms</th>
            <th className="border border-slate-300 px-2 py-1 text-center" colSpan={weeks.length || 1}>Heures supplémentaires par semaine <sup>(1)</sup></th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>Balance</th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>À récup.</th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>Congés payés</th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>Absences</th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>Primes en € net</th>
            <th className="border border-slate-300 px-2 py-1.5 text-center align-bottom" rowSpan={2}>Remarques</th>
          </tr>
          <tr className="bg-slate-100">
            {weeks.map((w) => (
              <th key={w} className="border border-slate-300 px-1.5 py-1 text-center tabular-nums font-medium">S{w}</th>
            ))}
            {weeks.length === 0 && <th className="border border-slate-300 px-1.5 py-1 text-center">—</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => <FicheRow key={l.employeeId} line={l} weeks={weeks} year={year} month={month} />)}
          {lines.length === 0 && (
            <tr><td colSpan={weeks.length + 7} className="border border-slate-300 text-center text-slate-400 py-4">Aucun salarié actif</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Une case se déclare — le calcul n'est qu'une proposition
// ============================================================================
// « Ça propose la réalité, maintenant tu déclares comme tu veux. Sinon on va se
// faire chier avec des règles interminables d'inclusion et d'exclusion. »
//
// Toute case calculée porte donc un crayon. Ce qui est déclaré à la main
// l'emporte, se voit (fond ambre + pastille), et se défait — la valeur du
// calcul reste affichée dans l'éditeur pour pouvoir y revenir.
type EditTarget =
  | { kind: 'week'; week: number }
  | { kind: 'balance' }
  | { kind: 'leaves' }
  | { kind: 'absences' };

/** Case modifiable : le crayon n'apparaît qu'au survol, et jamais à l'impression. */
function Cell({
  children, onEdit, overridden, hint, className = '',
}: {
  children: React.ReactNode;
  onEdit: () => void;
  overridden: boolean;
  /** Ce que le calcul proposait — pour ne pas avoir à ouvrir l'éditeur pour le savoir. */
  hint?: string;
  className?: string;
}) {
  return (
    <td
      className={`group/cell relative border border-slate-300 px-2 py-1.5 ${overridden ? 'bg-amber-50/70' : ''} ${className}`}
      title={overridden && hint ? `Valeur déclarée à la main. Le calcul proposait : ${hint}` : undefined}
    >
      {overridden && (
        <span className="no-print absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" title="Déclaré à la main" />
      )}
      {children}
      <button
        type="button"
        onClick={onEdit}
        title="Modifier cette case"
        className="no-print absolute top-0 right-0 p-0.5 rounded text-slate-300 opacity-0 group-hover/cell:opacity-100 hover:bg-slate-200 hover:text-slate-700 transition-opacity"
      >
        <Pencil size={10} />
      </button>
    </td>
  );
}

function FicheRow({ line: l, weeks, year, month }: { line: ShuttleEmployeeLine; weeks: number[]; year: number; month: number }) {
  const save = useSaveShuttleLine();
  const [primes, setPrimes] = useState(l.primes ? String(l.primes) : '');
  const [remarks, setRemarks] = useState(l.remarks ?? '');
  const [recover, setRecover] = useState(l.toRecover);
  const [editing, setEditing] = useState<EditTarget | null>(null);

  useEffect(() => {
    setPrimes(l.primes ? String(l.primes) : '');
    setRemarks(l.remarks ?? '');
    setRecover(l.toRecover);
  }, [l.employeeId, l.primes, l.remarks, l.toRecover]);

  const ov = l.overrides ?? {};
  // Balance = heures supp du mois + report du mois précédent, ou la valeur
  // déclarée : le serveur a déjà tranché, `l.balance` fait foi.
  const balance = l.balance;
  const autoRemark = l.absences.length === 0 && l.leaves.length === 0 && balance === 0 ? 'RAS' : '';

  function savePrimes() {
    const v = primes.trim() === '' ? 0 : parseFloat(primes.replace(',', '.'));
    if (!Number.isNaN(v) && v !== l.primes) save.mutate({ year, month, employeeId: l.employeeId, primes: v });
  }
  function saveRemarks() {
    if (remarks !== (l.remarks ?? '')) save.mutate({ year, month, employeeId: l.employeeId, remarks: remarks || null });
  }
  function toggleRecover() {
    const v = !recover;
    setRecover(v);
    save.mutate({ year, month, employeeId: l.employeeId, toRecover: v, overtimeHours: balance });
  }

  return (
    <>
      <tr className="align-top">
        <td className="border border-slate-300 px-2 py-1.5 font-medium whitespace-nowrap">
          {l.name}<span className="text-slate-400 font-normal"> · {l.contractHours}h</span>
        </td>
        {weeks.map((w) => {
          const ot = recover ? 0 : (l.weeklyOvertime[w] ?? 0);
          // Signé (R13) : positif = heures supp, négatif = heures dues (en moins).
          const cls = ot > 0 ? 'font-semibold text-primary' : ot < 0 ? 'font-semibold text-danger' : 'text-slate-400';
          return (
            <Cell
              key={w}
              overridden={ov.weeklyOvertime?.[String(w)] !== undefined}
              hint={`${l.computed?.weeklyOvertime?.[w] ?? 0} h`}
              onEdit={() => setEditing({ kind: 'week', week: w })}
              className={`px-1.5 text-center tabular-nums ${cls}`}
            >
              {ot > 0 ? `+${ot}` : ot < 0 ? `${ot}` : '0'}
            </Cell>
          );
        })}
        {weeks.length === 0 && <td className="border border-slate-300 text-center text-slate-400">—</td>}
        <Cell
          overridden={ov.balance !== undefined}
          hint={`${l.computed?.balance ?? 0} h`}
          onEdit={() => setEditing({ kind: 'balance' })}
          className="text-center tabular-nums whitespace-nowrap"
        >
          {/* « À récup. » coché : un 0 et RIEN d'autre — ni « à récup. 19h », ni
              cumul. Un chiffre sous le 0 se lisait comme des heures à payer.
              Le détail reste au survol, pour le titulaire. */}
          {recover ? (
            <div
              className="font-semibold text-slate-400"
              title={`Reporté au mois suivant : ${balance > 0 ? '+' : ''}${balance} h · cumul ${l.balanceAfter > 0 ? '+' : ''}${l.balanceAfter} h`}
            >
              0
            </div>
          ) : balance > 0 ? (
            <div>
              <div className="font-semibold text-primary">+{balance}h</div>
              {l.carriedIn > 0 && <div className="text-[9px] text-slate-400">dont +{l.carriedIn}h reporté</div>}
            </div>
          ) : balance < 0 ? (
            <div>
              <div className="font-semibold text-danger">{balance}h</div>
              <div className="text-[9px] text-slate-400">heures dues</div>
            </div>
          ) : (
            <span className="text-slate-300">—</span>
          )}
          {/* Solde cumulé : hors zone imprimée, la fiche officielle reste inchangée. */}
          {!recover && (
            <div
              className={`no-print text-[9px] mt-0.5 ${l.balanceAfter < 0 ? 'text-danger' : l.balanceAfter > 0 ? 'text-primary' : 'text-slate-400'}`}
              title={`Report des mois précédents : ${l.balanceBefore > 0 ? '+' : ''}${l.balanceBefore} h`}
            >
              cumul {l.balanceAfter > 0 ? '+' : ''}{l.balanceAfter} h
            </div>
          )}
        </Cell>
        <td className="border border-slate-300 px-2 py-1.5 text-center">
          {/* Cochable quelle que soit la balance : des heures dues se reportent
              aussi, et une semaine à −2 sur une ligne « à récup. » prêtait à
              confusion au cabinet. */}
          <input
            type="checkbox"
            checked={recover}
            onChange={toggleRecover}
            title="Ne rien régler sur la paie de ce mois : les heures (en plus ou en moins) sont reportées au mois suivant"
            className="rounded cursor-pointer"
          />
        </td>
        <Cell
          overridden={ov.leaves !== undefined}
          hint={`${l.computed?.leaves?.length ?? 0} période(s)`}
          onEdit={() => setEditing({ kind: 'leaves' })}
        >
          {l.leaves.length === 0 ? <span className="text-slate-300">—</span> : (
            <div className="space-y-0.5">
              {/* R14 : jour isolé « le DATE » vs période « du X au Y », SANS décompte (laissé au comptable). */}
              {l.leaves.map((c, i) => (
                <div key={i} className="whitespace-nowrap">
                  {c.from === c.to
                    ? <>le <strong>{fmt(c.from)}</strong></>
                    : <>du <strong>{fmt(c.from)}</strong> au <strong>{fmt(c.to)}</strong></>}
                </div>
              ))}
            </div>
          )}
        </Cell>
        <Cell
          overridden={ov.absences !== undefined}
          hint={`${l.computed?.absences?.length ?? 0} ligne(s)`}
          onEdit={() => setEditing({ kind: 'absences' })}
        >
          {l.absences.length === 0 ? <span className="text-slate-300">—</span> : (
            <div className="space-y-1">
              {l.absences.map((a, i) => (
                <div key={i}>
                  <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">{a.motif}</span>{' '}
                  <span className="whitespace-nowrap">du <strong>{fmt(a.from)}</strong> au <strong>{fmt(a.to)}</strong></span>
                  {a.hoursToDeduct > 0 && <span className="text-danger font-medium"> · à déduire : {a.hoursToDeduct}h</span>}
                </div>
              ))}
            </div>
          )}
        </Cell>
        <td className="border border-slate-300 px-1 py-1 text-center tabular-nums">
          <input
            value={primes}
            onChange={(e) => setPrimes(e.target.value)}
            onBlur={savePrimes}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            inputMode="decimal"
            placeholder="0"
            className="w-16 bg-transparent text-center tabular-nums rounded px-1 py-0.5 focus:outline-none focus:bg-primary-bg/40 hover:bg-slate-50"
          /> €
        </td>
        <td className="border border-slate-300 px-1 py-1">
          <RemarkField value={remarks} onChange={setRemarks} onSave={saveRemarks} placeholder={autoRemark} />
        </td>
      </tr>
      {editing && (
        <CellEditor
          line={l}
          target={editing}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(overrides) => {
            save.mutate({ year, month, employeeId: l.employeeId, overrides });
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Remarques : le texte revient à la ligne dans la case au lieu de défiler hors
 * de vue (« 128€ net de frais de parking » se lisait coupé). Entrée enregistre,
 * Maj+Entrée ajoute une ligne. La hauteur suit le texte.
 *
 * Sur papier, un simple texte prend la place du champ : la hauteur calculée à
 * l'écran ne vaut plus pour la largeur de la page imprimée, et le champ
 * couperait la fin de la remarque.
 */
function RemarkField({
  value, onChange, onSave, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  placeholder: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    // La colonne s'élargit ou se resserre avec la fenêtre : le texte se replie autrement.
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [value]);

  return (
    <>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
        }}
        placeholder={placeholder}
        className="no-print block w-full min-w-[120px] resize-none overflow-hidden break-words bg-transparent rounded px-1 py-0.5 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:bg-primary-bg/40 hover:bg-slate-50"
      />
      <div className="print-only px-1 whitespace-pre-line break-words text-slate-700">
        {value || <span className="text-slate-400">{placeholder}</span>}
      </div>
    </>
  );
}

// ============================================================================
// L'éditeur d'une case
// ============================================================================
// Une seule fenêtre pour les quatre sortes de cases. Elle affiche toujours ce
// que le CALCUL proposait, et garde un bouton « revenir au calcul » : une
// déclaration doit pouvoir se défaire, sinon on n'ose plus y toucher.

type OverridePatch = Parameters<ReturnType<typeof useSaveShuttleLine>['mutate']>[0]['overrides'];

function CellEditor({
  line: l, target, saving, onClose, onSubmit,
}: {
  line: ShuttleEmployeeLine;
  target: EditTarget;
  saving: boolean;
  onClose: () => void;
  onSubmit: (o: OverridePatch) => void;
}) {
  const computedWeek = target.kind === 'week' ? (l.computed?.weeklyOvertime?.[target.week] ?? 0) : 0;
  const currentWeek = target.kind === 'week' ? (l.weeklyOvertime[target.week] ?? 0) : 0;
  const [num, setNum] = useState(
    target.kind === 'week' ? String(currentWeek) : target.kind === 'balance' ? String(l.balance) : ''
  );
  const [leaves, setLeaves] = useState<ShuttleLeaveLine[]>(() => l.leaves.map((x) => ({ ...x })));
  const [absences, setAbsences] = useState<Omit<ShuttleAbsenceLine, 'type'>[]>(
    () => l.absences.map((a) => ({ motif: a.motif, from: a.from, to: a.to, hoursToDeduct: a.hoursToDeduct, comment: a.comment }))
  );
  const [error, setError] = useState<string | null>(null);

  const isOverridden =
    target.kind === 'week' ? l.overrides?.weeklyOvertime?.[String(target.week)] !== undefined
    : target.kind === 'balance' ? l.overrides?.balance !== undefined
    : target.kind === 'leaves' ? l.overrides?.leaves !== undefined
    : l.overrides?.absences !== undefined;

  const title =
    target.kind === 'week' ? `Semaine ${target.week}`
    : target.kind === 'balance' ? 'Balance du mois'
    : target.kind === 'leaves' ? 'Congés payés'
    : 'Absences';

  const computedLabel =
    target.kind === 'week' ? `${computedWeek} h`
    : target.kind === 'balance' ? `${l.computed?.balance ?? 0} h`
    : target.kind === 'leaves' ? `${l.computed?.leaves?.length ?? 0} période(s)`
    : `${l.computed?.absences?.length ?? 0} ligne(s)`;

  function submit() {
    if (target.kind === 'week' || target.kind === 'balance') {
      const v = parseFloat(num.replace(',', '.'));
      if (Number.isNaN(v)) { setError('Indiquez un nombre d’heures (ex. −3,5).'); return; }
      onSubmit(target.kind === 'week' ? { weeklyOvertime: { [String(target.week)]: v } } : { balance: v });
      return;
    }
    if (target.kind === 'leaves') {
      const bad = leaves.find((x) => !x.from || !x.to);
      if (bad) { setError('Chaque période a besoin d’une date de début et de fin.'); return; }
      onSubmit({ leaves });
      return;
    }
    const bad = absences.find((a) => !a.from || !a.to);
    if (bad) { setError('Chaque ligne a besoin d’une date de début et de fin.'); return; }
    onSubmit({ absences });
  }

  /** Efface la déclaration : la case retourne à ce que le calcul propose. */
  function reset() {
    onSubmit(
      target.kind === 'week' ? { weeklyOvertime: null }
      : target.kind === 'balance' ? { balance: null }
      : target.kind === 'leaves' ? { leaves: null }
      : { absences: null }
    );
  }

  return (
    <tr className="no-print">
      <td colSpan={99} className="p-0">
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto text-[13px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
            <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
            <p className="text-[12px] text-slate-500 mb-1">{l.name}</p>
            <p className="text-[11px] text-slate-400 mb-4">
              Le calcul propose <strong className="text-slate-600">{computedLabel}</strong>. Ce que vous écrivez ici part au cabinet à sa place.
            </p>

            {(target.kind === 'week' || target.kind === 'balance') && (
              <div>
                <div className="text-[11px] font-medium text-slate-500 mb-1">Heures</div>
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={num}
                    onChange={(e) => setNum(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                    inputMode="decimal"
                    className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 tabular-nums"
                  />
                  <span className="text-slate-500">h</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  Un nombre négatif = heures dues (en moins). <code>0</code> neutralise la case.
                </p>
              </div>
            )}

            {target.kind === 'leaves' && (
              <div className="space-y-2">
                {leaves.length === 0 && <p className="text-[12px] text-slate-400">Aucune période. La case restera vide.</p>}
                {leaves.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500 w-8">du</span>
                    <input type="date" value={c.from} onChange={(e) => setLeaves(upd(leaves, i, { from: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1" />
                    <span className="text-[11px] text-slate-500">au</span>
                    <input type="date" value={c.to} onChange={(e) => setLeaves(upd(leaves, i, { to: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1" />
                    <button type="button" onClick={() => setLeaves(leaves.filter((_, j) => j !== i))} className="p-1 rounded text-slate-400 hover:bg-danger-bg hover:text-danger" title="Retirer"><Trash2 size={13} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setLeaves([...leaves, { from: '', to: '', days: 0 }])} className="flex items-center gap-1 text-[12px] text-primary font-medium hover:underline">
                  <Plus size={13} /> Ajouter une période
                </button>
              </div>
            )}

            {target.kind === 'absences' && (
              <div className="space-y-3">
                {absences.length === 0 && <p className="text-[12px] text-slate-400">Aucune ligne. La case restera vide.</p>}
                {absences.map((a, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        value={a.motif}
                        onChange={(e) => setAbsences(upd(absences, i, { motif: e.target.value }))}
                        placeholder="Motif (texte libre)"
                        className="flex-1 rounded-lg border border-slate-300 px-2 py-1"
                      />
                      <button type="button" onClick={() => setAbsences(absences.filter((_, j) => j !== i))} className="p-1 rounded text-slate-400 hover:bg-danger-bg hover:text-danger" title="Retirer"><Trash2 size={13} /></button>
                    </div>
                    {/* Dates et heures sur DEUX lignes : sur une seule, « à
                        déduire » se retrouvait séparé de son champ par un
                        retour à la ligne, et la phrase se lisait de travers. */}
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500 w-6">du</span>
                      <input type="date" value={a.from} onChange={(e) => setAbsences(upd(absences, i, { from: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1" />
                      <span className="text-[11px] text-slate-500">au</span>
                      <input type="date" value={a.to} onChange={(e) => setAbsences(upd(absences, i, { to: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500">à déduire</span>
                      <input
                        value={String(a.hoursToDeduct ?? 0)}
                        onChange={(e) => setAbsences(upd(absences, i, { hoursToDeduct: parseFloat(e.target.value.replace(',', '.')) || 0 }))}
                        inputMode="decimal"
                        className="w-16 rounded-lg border border-slate-300 px-2 py-1 tabular-nums"
                      />
                      <span className="text-[11px] text-slate-500">h</span>
                      <span className="text-[11px] text-slate-400">— ce que le cabinet retire de la paie</span>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => setAbsences([...absences, { motif: '', from: '', to: '', hoursToDeduct: 0, comment: null }])} className="flex items-center gap-1 text-[12px] text-primary font-medium hover:underline">
                  <Plus size={13} /> Ajouter une ligne
                </button>
              </div>
            )}

            {error && <p className="text-[12px] text-danger mt-3">{error}</p>}

            <div className="flex items-center justify-between gap-2 mt-6">
              {isOverridden ? (
                <Button variant="ghost" size="sm" icon={RotateCcw} onClick={reset} loading={saving}>Revenir au calcul</Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
                <Button variant="primary" size="sm" onClick={submit} loading={saving}>Enregistrer</Button>
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

/** Remplace l'élément `i` d'une liste sans la muter. */
function upd<T>(list: T[], i: number, patch: Partial<T>): T[] {
  return list.map((x, j) => (j === i ? { ...x, ...patch } : x));
}
